import {shortDocumentPattern} from '../../utils/documentShortCode';
import React,{ createContext,CSSProperties,ReactNode,useContext,useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isErpPath } from '../../utils/erpRoutes';
import { applySiteIdentity } from '../../utils/siteIdentity';
import { createPreference } from '../interaction/runtime';
import { base,canvas } from '../styles/colors';

export type Theme = 'dark' | 'light' | 'system';
export const THEME_STORAGE_KEY = 'sseeaa-theme';
const SYSTEM_QUERY = '(prefers-color-scheme: dark)';
const isTheme = (value: unknown): value is Theme => value === 'dark' || value === 'light' || value === 'system';
function readTheme(): Theme {
    try { const saved = localStorage.getItem(THEME_STORAGE_KEY); return isTheme(saved) ? saved : 'dark'; }
    catch { return 'dark'; }
}
function applyTheme(theme: Theme) {
    const documentTheme = shortDocumentPattern.test(window.location.pathname) ? (document.documentElement.dataset.documentTheme === 'dark' || /^\/live-(session|purge)-/.test(window.location.pathname) ? 'dark' : 'light') : null;
    const resolved = documentTheme ?? (theme === 'system' ? (window.matchMedia(SYSTEM_QUERY).matches ? 'dark' : 'light') : theme);
    /* Document theme is independent of the saved ERP preference. */
    document.documentElement.dataset.theme = resolved;
    document.documentElement.dataset.themePreference = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', documentTheme==='dark' ? `rgb(${base[':root']['--ui-ink']})` : canvas[resolved]);
}
export function initializeTheme() { applyTheme(readTheme()); }
const themePreference = createPreference<Theme>(THEME_STORAGE_KEY, readTheme, value => localStorage.setItem(THEME_STORAGE_KEY, value));
const ThemeContext = createContext<{ theme: Theme; setTheme: (theme: Theme) => void } | null>(null);
export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const theme = themePreference.useValue();
    const setTheme = themePreference.set;
    useLayoutEffect(() => {
        applyTheme(theme);
        const media = window.matchMedia(SYSTEM_QUERY);
        const onChange = () => { if (theme === 'system') applyTheme(theme); };
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, [theme]);
    return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}
export function useTheme() {
    const value = useContext(ThemeContext);
    if (!value) throw new Error('useTheme must be used within ThemeProvider');
    return value;
}

/** Document scope is intentional: menus and drawers rendered into body inherit the brand. */
export function BrandTheme({ brand, children }: { brand?: 'withpot' | 'logistics'; children?: ReactNode } = {}) {
    const { pathname } = useLocation();
    const {theme}=useTheme();
    useLayoutEffect(() => {
        if (brand) return;
        applyTheme(theme);
        applySiteIdentity(pathname);
        document.documentElement.dataset.brand = (isErpPath(pathname) || /^\/supplier(?:\/|$)/.test(pathname)) ? 'withpot' : 'logistics';
    }, [pathname, brand, theme]);
    if (!brand) return null;
    const palette = brand === 'withpot' ? base[":root[data-brand='withpot']"] : base[':root'];
    const variables = Object.fromEntries(Object.entries(palette).filter(([name]) => name.startsWith('--ui-brand')));
    return <div data-brand-preview={brand} style={variables as CSSProperties}>{children}</div>;
}
